import React from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

const WhyInsuranceClaimsRejectedArticle = () => (
    <Box>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.65rem', md: '2rem' } }}>
            Why Insurance Claims Get Rejected in Home Rehab
        </Typography>
        <Typography color="text.secondary" fontWeight={600} paragraph sx={{ fontStyle: 'italic' }}>
            Free-form notes filled in later will fail.
        </Typography>
        <Typography color="text.secondary" paragraph>
            <strong>TL;DR:</strong> Home rehab insurance claims get rejected because documentation is incomplete, free-form, and
            filled in after the fact, not because sessions weren&apos;t delivered. Insurers need structured records with
            required fields, completed at point of delivery. Reminders don&apos;t fix this because they don&apos;t change what
            the system accepts. The fix is enforcement at point of delivery, not better instructions to the therapy team.
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ fontStyle: 'italic' }}>
            —
        </Typography>
        <Typography color="text.secondary" paragraph>
            A claim comes back rejected. The centre owner goes through the session records and finds that the therapist did
            visit, the patient was seen, the session happened. The visit isn&apos;t in question. The documentation is.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The notes are there - a paragraph describing what was done, filled in a day or two after the visit. But they&apos;re
            incomplete. Key fields are missing. The format doesn&apos;t meet what the insurer needs to process the claim. The
            centre tries to add the missing information retrospectively. The insurer doesn&apos;t accept it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The claim stays rejected. The centre loses the revenue. And the ops head sends another message to the therapy team
            reminding them to complete their notes properly.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Next month, the same thing happens.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why reminders don&apos;t fix the problem
        </Typography>
        <Typography color="text.secondary" paragraph>
            The instinct when claims get rejected is to assume the therapists aren&apos;t being careful enough. So the centre
            adds a reminder - complete your notes the same day, use this format, don&apos;t leave fields blank. The team
            acknowledges it. Documentation improves briefly. Then it drifts back, and the rejections continue.
        </Typography>
        <Typography color="text.secondary" paragraph>
            This happens because the reminder addresses the behaviour without changing the structure. A therapist completing
            free-form notes on a personal phone, two days after a visit, from memory, is working within a system that makes
            incomplete documentation almost inevitable. The reminder asks them to do better within that system. It doesn&apos;t
            change the system.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The problem isn&apos;t that therapists are careless. It&apos;s that free-form retrospective documentation is
            structurally unreliable. It depends on memory, goodwill, and consistency - none of which hold at scale across a
            field team delivering multiple sessions a day.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What structured clinical documentation actually means
        </Typography>
        <Typography color="text.secondary" paragraph>
            Structured clinical documentation isn&apos;t about length or detail. It&apos;s about format and timing.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Format means required fields - specific, non-optional entries that capture what was assessed, what was treated, and
            what the plan is for the next session. Not a blank text box where the therapist writes whatever they remember.
            Specific fields that must be completed before the record is saved.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Timing means on-site, at point of delivery. Before the therapist moves to the next patient. Not that evening. Not
            the next morning. At the moment the session ends, while the detail is accurate and the record can be confirmed.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The difference between free-form retrospective notes and structured on-site documentation isn&apos;t cosmetic. A
            free-form note filled in two days later is a recollection. A structured record completed on-site is a confirmed
            account of what happened. One can be disputed. The other is considerably harder to challenge.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            The difference between free-form notes and structured documentation
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ my: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }} />
                        <TableCell sx={{ fontWeight: 700 }}>Free-form retrospective</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Structured on-site</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[
                        [
                            'Completeness',
                            'Whatever the therapist remembered to include',
                            'Required fields enforced before the record saves',
                        ],
                        ['Format', 'Varies by therapist and session', 'Standardised across the entire team'],
                        [
                            'Timing',
                            'Filled in after the fact, sometimes days later',
                            'Completed before the therapist leaves the patient',
                        ],
                        [
                            'Audit-readiness',
                            'Inconsistent, difficult to defend',
                            'Consistent, reviewable, linked to the session record',
                        ],
                    ].map(([label, a, b]) => (
                        <TableRow key={label}>
                            <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                            <TableCell>{a}</TableCell>
                            <TableCell>{b}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What a rejected claim actually looks like
        </Typography>
        <Typography color="text.secondary" paragraph>
            A home rehab centre submits a claim for a session delivered three weeks earlier. The insurer reviews the
            documentation and flags several problems. The assessment findings aren&apos;t recorded in a structured format. The
            treatment delivered is described in general terms without the specificity needed to justify the claim. There&apos;s
            no record of what the plan is for continuation of care.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The centre goes back to the therapist. The therapist recalls the session. The patient had limited mobility,
            specific exercises were done, progress was made. All of that gets written up and resubmitted. The insurer reviews
            it again. The retrospective addition doesn&apos;t resolve the issue. The original record didn&apos;t capture what
            was needed at the time it was needed, and a reconstruction weeks later doesn&apos;t change that.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The claim stays rejected. The session was delivered. The revenue is lost.
        </Typography>
        <Typography color="text.secondary" paragraph>
            This is the pattern that surfaces when documentation is free-form and retrospective. It&apos;s not an occasional
            problem. For centres submitting claims at volume, it&apos;s a recurring one.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/why-whatsapp-breaks-field-operations-system">Why WhatsApp Breaks as a Field Operations System</Link>.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            The enforcement versus reminder distinction
        </Typography>
        <Typography color="text.secondary" paragraph>
            A reminder system asks therapists to complete documentation properly. An enforcement mechanism makes it
            structurally impossible to skip required fields.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When documentation lives in a WhatsApp message or a free-form notes field, there&apos;s nothing to enforce. The
            therapist either fills it in or doesn&apos;t. If they don&apos;t, the record doesn&apos;t exist until someone chases
            it. If they do, it may still be missing what the insurer needs.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When documentation is structured - required fields, completed on-site, before the therapist moves on - there&apos;s
            nothing to remind. The record either exists with the required information or it doesn&apos;t exist at all. A
            therapist can&apos;t submit an incomplete record because the system won&apos;t accept one.
        </Typography>
        <Typography color="text.secondary" paragraph>
            This is the structural change that reduces claim rejections. Not better reminders. Not more detailed guidelines. A
            system that enforces documentation structure at the moment it matters.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What meDZhub enforces at point of delivery
        </Typography>
        <Typography color="text.secondary" paragraph>
            meDZhub requires therapists to complete structured documentation on-site before a session record is saved.
            Required fields - what was assessed, what was treated, what the continuation plan is - can&apos;t be skipped. The
            record is timestamped and stored independently of any message thread.
        </Typography>
        <Typography color="text.secondary" paragraph>
            That record becomes the direct input to insurance submissions. When a claim is prepared, the documentation is
            already structured, already complete, and already tied to a verified record of the session. There&apos;s no
            reconstruction step. There&apos;s no chasing therapists for information they should have captured three weeks
            ago.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The structural causes of rejection - incomplete fields, free-form format, retrospective completion - are removed
            by the system rather than managed through reminders.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/session-verification-payout-cycle-home-rehab">
                How Session Verification Changes Your Payout Cycle
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            The cost of the current pattern
        </Typography>
        <Typography color="text.secondary" paragraph>
            Every rejected claim represents a session that was delivered but not paid for. At low volume, this is manageable.
            A centre submitting 30 to 40 claims a month and absorbing three to five rejections per cycle is losing that
            revenue on sessions that were genuinely delivered. The sessions happened. The records didn&apos;t hold.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The admin time spent chasing documentation, resubmitting claims, and managing the back-and-forth with insurers
            compounds that cost. Most centres don&apos;t track it explicitly, which means the full picture is rarely visible.
            What gets tracked is the frustration - the rejections, the resubmissions, the conversations that keep happening
            about notes that keep being incomplete.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Changing the documentation structure doesn&apos;t just reduce rejections. It removes the recurring operational
            burden that comes with managing a system that was never designed to produce audit-ready records.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/privacy">Privacy policy — patient data governance and DPDP</Link>.
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ mt: 2 }}>
            If your documentation system depends on reminders, it will keep producing the same results.
        </Typography>
        <Button component={Link} to="/demo" variant="contained" sx={{ mt: 1, mb: 4 }} data-gtm="Blog-insurance-demo">
            Book a demo
        </Button>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 2, mb: 2 }}>
            Frequently asked questions
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Why do home rehab insurance claims keep getting rejected even when sessions were delivered?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Because the session happening and the documentation meeting submission requirements are two different things.
            Insurers need structured records that capture specific information in a specific format. Free-form notes filled in
            retrospectively typically don&apos;t meet that standard, regardless of whether the session was delivered correctly.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What is the difference between free-form session notes and structured clinical documentation?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Free-form notes are written by the therapist after the fact, in whatever format they choose, with no required
            fields. Structured documentation has specific required fields completed on-site at point of delivery. The first
            depends on memory and consistency. The second is enforced by the system.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            Why don&apos;t reminders to complete notes properly fix the claim rejection problem?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Because the problem is structural, not behavioural. Free-form retrospective entry allows incomplete records
            regardless of intent. A reminder doesn&apos;t change what the system accepts. An enforcement mechanism does.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            How do I make sure session records are defensible before submission?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Documentation needs to be structured, with required fields, completed on-site at point of delivery. Retrospective
            additions to an incomplete record don&apos;t resolve the structural gap. The record needs to exist in the right
            format from the moment the session ends.
        </Typography>
    </Box>
);

export default WhyInsuranceClaimsRejectedArticle;
